import React, { useEffect, useState } from 'react';
import { Btn, Msg, panelGet, panelPost } from '../kit';
import { Labeled, inputCls } from './emailKit';
import { announceDefaultFallbacks } from './content/ContentEditor';

/**
 * Default fallbacks — what {first_name}, {name} and {plan_name} become for
 * someone with no value, in every e-mail that has no fallback of its own
 * (owner, 2026-10-27: "member" instead of "there", changeable once for all).
 * A template's own fallbacks (Templates (editable), Saved templates), a
 * campaign's and a MEMAC trigger's win over these. Backend:
 * email_templates.admin_default_fallbacks / admin_save_default_fallbacks.
 */

type Item = { name: string; meaning: string; value: string; default: string };

const DefaultFallbacks: React.FC<{ open?: boolean; onSaved?: () => void }> = ({ open, onSaved }) => {
  const [items, setItems] = useState<Item[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loadErr, setLoadErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const take = (data: any) => {
    const rows: Item[] = (Array.isArray(data?.items) ? data.items : []).map((r: any) => ({
      name: String(r?.name ?? ''), meaning: String(r?.meaning ?? ''),
      value: String(r?.value ?? ''), default: String(r?.default ?? ''),
    })).filter((r: Item) => r.name);
    setItems(rows);
    setDraft(Object.fromEntries(rows.map(r => [r.name, r.value])));
    if (data?.fallbacks) announceDefaultFallbacks(data.fallbacks);
  };

  useEffect(() => {
    panelGet('email', 'default_fallbacks').then(({ data, error }) => {
      if (error) setLoadErr(error); else take(data);
    });
  }, []);

  const dirty = items.some(r => (draft[r.name] ?? '') !== r.value);
  const save = async () => {
    setBusy(true); setMsg(null);
    const { data, error } = await panelPost('email', 'save_default_fallbacks', {
      fallbacks: Object.fromEntries(items.map(r => [r.name, (draft[r.name] ?? '').trim()])),
    });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    take(data);
    onSaved?.();
    setMsg({ kind: 'ok', text: 'Saved. Every e-mail without a fallback of its own uses these from the next send.' });
  };

  const summary = items.length
    ? items.map(r => `{${r.name}} → “${r.value || r.default}”`).join(' · ')
    : '';

  return (
    <details className="rounded-lg border border-grayscale-300 bg-white px-3 py-2 text-xs" open={open}>
      <summary className="cursor-pointer text-grayscale-1300">
        <b>Default fallbacks</b> — for every e-mail{summary ? `: ${summary}` : ''}
      </summary>
      <div className="mt-2 flex flex-col gap-2">
        <p className="text-grayscale-1100">
          What a placeholder becomes for someone with no value, in every e-mail (built-in, Campaigns, MEMAC) that
          has no fallback of its own. A template, campaign or trigger can still set its own. Leave a box blank to
          use the built-in value.
        </p>
        {loadErr && <Msg kind="error">{loadErr}</Msg>}
        <div className="grid sm:grid-cols-3 gap-2">
          {items.map(r => (
            <Labeled key={r.name} label={`{${r.name}} — ${r.meaning}`} hint={`blank = “${r.default}”`}>
              <input value={draft[r.name] ?? ''} maxLength={200} placeholder={r.default} disabled={busy}
                onChange={e => setDraft(d => ({ ...d, [r.name]: e.target.value }))} className={inputCls} />
            </Labeled>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Btn kind="primary" small onClick={save} disabled={!dirty || busy || !items.length}>
            {busy ? 'Saving…' : 'Save default fallbacks'}
          </Btn>
          {dirty && <Btn small onClick={() => setDraft(Object.fromEntries(items.map(r => [r.name, r.value])))}
            disabled={busy}>Discard</Btn>}
        </div>
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      </div>
    </details>
  );
};

export default DefaultFallbacks;
